import {Component, computed, effect, inject, signal, input, output} from '@angular/core';

import {TranslocoService} from '@jsverse/transloco';
import {EvaluationSubCriteriaImportanceScore} from '@frontend/shared-api';
import {ImportanceIconComponent} from '../importance-icon/importance-icon.component';
import {ModelFieldSelectComponent} from '../inputs/model-field/model-field-select/model-field-select.component';
import {IMPORTANCE_SCORE_LABEL_KEYS} from '../../constants/importance-score.constants';

export interface ImportanceScoreOption {
  label: string;
  value: EvaluationSubCriteriaImportanceScore;
}

@Component({
  selector: 'app-importance-score-selector',
  imports: [ImportanceIconComponent, ModelFieldSelectComponent],
  templateUrl: './importance-score-selector.component.html',
})
export class ImportanceScoreSelectorComponent {
  private readonly transloco = inject(TranslocoService);

  readonly selectedValue = input<EvaluationSubCriteriaImportanceScore>();
  readonly placeholder = input('evaluation.selectImportanceScore');
  readonly disabled = input(false);
  readonly width = input('300px');
  readonly testId = input('');

  readonly valueChange = output<EvaluationSubCriteriaImportanceScore | undefined>();

  private readonly selectedValueSignal = signal<EvaluationSubCriteriaImportanceScore | undefined>(
    undefined,
  );

  constructor() {
    this.selectedValueSignal.set(this.selectedValue());

    effect(() => {
      this.selectedValueSignal.set(this.selectedValue());
    });
  }

  protected readonly importanceScoreOptions = computed((): ImportanceScoreOption[] =>
    [
      EvaluationSubCriteriaImportanceScore.BASIC_ATTRIBUTE,
      EvaluationSubCriteriaImportanceScore.PERFORMANCE_ATTRIBUTE,
      EvaluationSubCriteriaImportanceScore.ENTHUSIASM_ATTRIBUTE,
      EvaluationSubCriteriaImportanceScore.INDIFFERENT_ATTRIBUTE,
      EvaluationSubCriteriaImportanceScore.NOT_RELEVANT,
    ].map((importanceScore) => ({
      label: this.transloco.translate(IMPORTANCE_SCORE_LABEL_KEYS[importanceScore]),
      value: importanceScore,
    })),
  );

  protected readonly selectedImportanceScore = computed(() => this.selectedValueSignal());

  onValueChange(value: EvaluationSubCriteriaImportanceScore | null): void {
    const selectedValue: EvaluationSubCriteriaImportanceScore | undefined = value ?? undefined;

    this.selectedValueSignal.set(selectedValue);
    this.valueChange.emit(selectedValue);
  }
}
